import {
	IOperationResultNoValue,
	IRichContentEditorState,
	IVirtualLocationConfig,
	MeetingConfigViewModel,
	MeetingLocation,
	MeetingType,
} from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useErrorMessages, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useLambda } from '../../../../../../models/hooks/useLambda';
import { useEventLogging } from '../../../../../../models/Logging';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	createRichContentEditorStateWithText,
	getFormattedAddress,
	isValidEmail,
} from '../../../../../../models/UiUtils';
import { getNormalizedEmailProvider } from '../../../../../../models/utils/emailProviderUtils';
import { MeetingLocationPlaceholder, MeetingVirtualLocation, TokenAttributes } from '../../../../../../models/Token';
import { Input } from '../../../../../components-v2/Input';
import { Checkbox } from '../../../../Checkbox';
import { RadioButton } from '../../../../RadioButton';
import { RichContentDocumentEditor } from '../../../../richContent/RichContentDocumentEditor';
import { InfoIcon } from '../../../../svgs/icons/InfoIcon';
import { TextInput } from '../../../../TextInput';
import { TinyPopover } from '../../../../TinyPopover';
import { styleSheet } from '../styles';

interface IProps {
	meeting: MeetingConfigViewModel;
	onEmailError: (message: string) => void;
}

const RawRichTextContentBlockStyleAttribute = 'margin:0;padding:0;font-size:14px;font-family:arial,sans-serif;';

const defaultEditorConfig = {
	defaultBlockStyleAttributeStringValue: RawRichTextContentBlockStyleAttribute,
	imageOptions: { base64EmbedOnInsert: true },
	placeholders: [] as any[],
	plugins: ['placeholders'],
	toolbar: 'meetingmergefieldmenu',
};

export const BasicInfo: React.FC<IProps> = observer(({ meeting, onEmailError }) => {
	const logger = useEventLogging();
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const [meetingName, setMeetingName] = useState(meeting?.name ?? '');
	const [meetingNameError, setMeetingNameError] = useState('');
	const [meetingLocation, setMeetingLocation] = useState<MeetingType>(
		meeting?.locationConfig?._type ?? MeetingType.Phone
	);

	const [meetingNote, setMeetingNote] = useState<IRichContentEditorState>(
		meeting?.confirmationNote
			? convertRawRichTextContentStateToRichContentEditorState(meeting.confirmationNote)
			: createRichContentEditorStateWithText('', RawRichTextContentBlockStyleAttribute)
	);
	const [phonePopoverIsOpen, , setPhonePopoverIsOpen] = useLambda(false);
	const [ccEmail, setCcEmail] = useState(meeting?.ccEmails?.join(', ') ?? '');
	const [ccEmailError, setCcEmailError] = useState('');
	const [showOnHomepage, setShowOnHomepage] = useState(meeting?.showOnHomepage);
	const [addMeetingNameToCalendarEvent, setAddMeetingNameToCalendarEvent] = useState(
		meeting?.addMeetingNameToCalendarEvent
	);
	const [address, setAddress] = useState(
		meeting?.getDefaultAddress() ||
			(userSession?.account?.planDetails?.address ? getFormattedAddress(userSession.account.planDetails.address) : '')
	);
	const [virtualLink, setVirtualLink] = useState(meeting?.getDefaultVirtualLink());
	const [generateUniqueLink, setGenerateUniqueLink] = useState(
		meeting?.locationConfig?._type === MeetingType.Virtual
			? ((meeting.locationConfig as IVirtualLocationConfig).generateUniqueLink ?? false)
			: false
	);

	// Unique meeting links are supported for Office 365 / Outlook (Microsoft Teams) and Google / Gmail (Google Meet)
	// users. Gate the option on the meeting owner's provider (forUser when configuring for a team member, otherwise
	// the session user). getNormalizedEmailProvider is a passthrough for these inputs ('Office365'/'Google' stay as
	// is; 'Outlook'/'Gmail' have no case and fall through to default unchanged), so the comparisons are all live.
	const provider = getNormalizedEmailProvider(meeting?.forUser?.emailProvider ?? userSession?.user?.emailProvider);
	const isOffice365 = provider === 'Office365' || provider === 'Outlook';
	const isGoogle = provider === 'Google' || provider === 'Gmail';
	// generateUniqueLink is accepted by the API for these four providers only; the checkbox is hidden for the rest
	// (the backend also rejects them on commit).
	const supportsUniqueLink = isOffice365 || isGoogle;
	// Provider-aware display name — the underlying field is provider-neutral, so this is purely a label choice.
	const uniqueLinkProviderName = isOffice365 ? 'Microsoft Teams' : isGoogle ? 'Google Meet' : 'video meeting';

	useEffect(() => {
		meeting.setAsEditing()?.catch((err: IOperationResultNoValue) => {
			logger.logApiError('MeetingConfigSetAsEditing-Error', err);
			// do not need to show user that this failed...
		});
		meeting.getAutomationChoices();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onShowOnHomepageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.checked;
		setShowOnHomepage(value);
		meeting.showOnHomepage = value;
	};

	const onAddMeetingNameToCalendarEventChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.checked;
		setAddMeetingNameToCalendarEvent(value);
		meeting.addMeetingNameToCalendarEvent = value;
	};

	const onLocationChange = useCallback(
		(location: MeetingType) => () => {
			setMeetingLocation(location);

			switch (location) {
				case MeetingType.Phone:
					meeting.locationConfig = {
						_type: location,
						willCall: true,
					};
					break;
				case MeetingType.InPerson:
					meeting.locationConfig = {
						_type: location,
						address,
					};
					break;
				case MeetingType.Virtual:
					meeting.locationConfig = {
						_type: location,
						link: virtualLink,
						generateUniqueLink,
					};
					break;
				case MeetingType.AllowInvitee:
					meeting.locationConfig = {
						_type: location,
						locations: [],
					};
					break;
				default:
					break;
			}
		},
		[address, meeting, virtualLink, generateUniqueLink]
	);

	const onChangeGenerateUniqueLink = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setGenerateUniqueLink(checked);
		// Spread the existing Virtual config so any future IVirtualLocationConfig fields aren't dropped; keep
		// link in sync with the current input value and apply the new flag.
		meeting.locationConfig = {
			...(meeting.locationConfig as IVirtualLocationConfig),
			_type: MeetingType.Virtual,
			link: virtualLink,
			generateUniqueLink: checked,
		};
	};

	const onAllowInviteeConfigUpdated = (location: MeetingLocation) => (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.checked) {
			updateAllowInviteeLocation(location)();
			return;
		}
		meeting.deleteAllowInviteeLocation(location);
	};

	const updateAllowInviteeLocation = (location: MeetingLocation) => async () => {
		try {
			switch (location) {
				case MeetingLocation.Phone:
					await meeting.updateAllowInviteeLocation({
						_type: location,
						phoneNumber: '',
					});
					break;
				case MeetingLocation.InPerson:
					await meeting.updateAllowInviteeLocation({
						_type: location,
						address,
					});
					break;
				case MeetingLocation.Virtual:
					await meeting.updateAllowInviteeLocation({
						_type: location,
						link: virtualLink,
					});
					break;
				default:
					break;
			}
		} catch (err) {
			logger.logApiError('MeetingConfigUpdateAllowInvitee-Error', err);

			errorMessages.pushApiError(err);
		}
	};

	const onMeetingNoteBlur = useCallback(() => {
		const richTextContent = meetingNote.getRawRichTextContent();
		if (richTextContent.document !== meeting?.confirmationNote?.document) {
			meeting.confirmationNote = richTextContent;
		}
	}, [meeting, meetingNote]);

	const onMeetingNoteStateChange = useCallback((contentState: IRichContentEditorState) => {
		setMeetingNote(contentState);
	}, []);

	/**
	 * @todo: this method on blur updates the meeting name but
	 * it creates a race condition with the meeting name update in the meeting config modal.
	 * packages/main/app/extViewmodels/viewModels/index.ts:709 was updated to avoid this race condition.
	 * we should update this component not to use the meeting view model but instead use react query to update the meeting name.
	 */
	const onNameBlur = useCallback(() => {
		if (!meetingName) {
			setMeetingNameError('a meeting name is required.');
		} else if (meeting && meetingName !== meeting.name) {
			meeting.name = meetingName;
		}
	}, [meeting, meetingName]);

	const onNameChanged = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setMeetingName(e.target.value);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[meeting]
	);

	const onCcEmailBlur = useCallback(() => {
		const ccEmailsArray = ccEmail
			.split(',')
			.map(cc => cc.trim())
			.filter(cc => !!cc);
		const omit = ccEmailsArray?.filter(x => !isValidEmail(x));
		const valid = ccEmailsArray?.filter(x => isValidEmail(x));

		if (omit?.length > 0) {
			onEmailError('A valid email is required');
			setCcEmailError('A valid email is required');
		} else if (meeting) {
			onEmailError('');
			setCcEmailError('');
			meeting.ccEmails = valid;
		}
	}, [meeting, ccEmail, onEmailError]);

	const onCcEmailChanged = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setCcEmail(e.target.value);
	}, []);

	const onPhonePopoverHover = (isOpen: boolean) => () => {
		setPhonePopoverIsOpen(isOpen)();
	};

	const onChangeAddress = (e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.target.value);
	const onChangeVirtualLink = (e: React.ChangeEvent<HTMLInputElement>) => setVirtualLink(e.target.value);

	const isMeetingAllowInvitee = meetingLocation === MeetingType.AllowInvitee;
	const isMeetingInPerson = meetingLocation === MeetingType.InPerson;
	const isMeetingLocationPhone = meetingLocation === MeetingType.Phone;
	const isVirtualMeeting = meetingLocation === MeetingType.Virtual;

	// When a unique link is auto-generated, the join details are added to the calendar invite by the provider and
	// the location merge field in the body renders a placeholder (the link doesn't exist when the body is composed).
	// Surface a non-blocking hint if the body still contains the location merge field so the user can move it to the
	// confirmation/reminder emails, where it resolves correctly.
	const meetingNoteDocument = meetingNote?.getRawRichTextContent()?.document ?? '';
	// The location and virtual-meeting-link merge fields both resolve to link/location info that doesn't exist when
	// the invite body is composed (the provider generates the unique link at booking time), so either one in the
	// body is redundant for these meeting types. Detect whichever was used — inserted via the toolbar (serialized
	// as a placeholder span carrying data-placeholder-value) or typed literally as its symbol (the literal case is
	// the most confusion-prone, so catch it too even though this is only a non-blocking hint).
	const meetingNoteHasRedundantLinkToken = [MeetingLocationPlaceholder, MeetingVirtualLocation].some(placeholder => {
		if (meetingNoteDocument.includes(`${TokenAttributes.DataPlaceholderValue}="${placeholder.value}"`)) {
			return true;
		}
		return [placeholder.symbol, ...(placeholder.additionalSymbols ?? [])].some(
			symbol => !!symbol && meetingNoteDocument.includes(symbol)
		);
	});
	const showGenerateUniqueLinkHint = isVirtualMeeting && generateUniqueLink && meetingNoteHasRedundantLinkToken;

	return (
		<div className={css(styleSheet.col)}>
			<div className={css(styleSheet.card)}>
				<div className={css(styleSheet.sectionTitle)}>Basic Info</div>
				<div className={css(styleSheet.sectionWrapper)}>
					<p className={css(styleSheet.label)}>
						Meeting Name
						{meetingNameError && <span className={css(styleSheet.error)}>{meetingNameError}</span>}
					</p>
					<Input
						id='meeting-config-name-input'
						onBlur={onNameBlur}
						onChange={onNameChanged}
						type='text'
						value={meetingName}
					/>
				</div>
				<div className={css(styleSheet.sectionWrapper)}>
					<div className={css(styleSheet.checkboxContainer)}>
						<Checkbox checked={showOnHomepage} id='meeting-config-show-on-homepage' onChange={onShowOnHomepageChange}>
							<span className={css(styleSheet.checkboxText)}>Add to meetings homepage</span>
						</Checkbox>
					</div>
					<div className={css(styleSheet.checkboxContainer)}>
						<Checkbox
							checked={addMeetingNameToCalendarEvent}
							id='meeting-config-meeting-name-in-calendar-invite'
							onChange={onAddMeetingNameToCalendarEventChange}
						>
							<span className={css(styleSheet.checkboxText)}>Include meeting name in calendar invitation</span>
						</Checkbox>
					</div>
				</div>
				<div className={css(styleSheet.sectionWrapper)}>
					<p className={css(styleSheet.label)}>Meeting Location</p>
					<RadioButton
						checked={isMeetingLocationPhone}
						className={css(styleSheet.radio)}
						disabled={meeting?.isBusy || meeting?.isSaving}
						id='meeting-config-location-phone'
						name='meeting-config-location'
						onChange={onLocationChange(MeetingType.Phone)}
					>
						<span className={css(styleSheet.phoneLabel)}>
							Phone call
							<TinyPopover
								anchor={
									<span
										className={css(styleSheet.phoneLabelIcon)}
										onMouseEnter={onPhonePopoverHover(true)}
										onMouseLeave={onPhonePopoverHover(false)}
									>
										<InfoIcon />
									</span>
								}
								isOpen={phonePopoverIsOpen}
								align='center'
								placement={['right']}
							>
								<div className={css(styleSheet.label, styleSheet.phoneInfo)}>
									We&apos;ll ask the client to leave a number and let them know you will call them.
								</div>
							</TinyPopover>
						</span>
					</RadioButton>
					<RadioButton
						checked={isMeetingInPerson}
						className={css(styleSheet.radio)}
						disabled={meeting?.isBusy || meeting?.isSaving}
						id='meeting-config-location-in-person'
						name='meeting-config-location'
						onChange={onLocationChange(MeetingType.InPerson)}
					>
						<span>In Person</span>
					</RadioButton>
					{isMeetingInPerson && (
						<div className={css(styleSheet.locationInfoContainer)}>
							<p className={css(styleSheet.label)}>Please enter the physical meeting address below:</p>
							<TextInput
								className={css(styleSheet.locationInfoInput)}
								type='text'
								inputId='meeting-config-location-in-person-text'
								value={address}
								onChange={onChangeAddress}
								onBlur={onLocationChange(MeetingType.InPerson)}
							/>
						</div>
					)}
					<RadioButton
						checked={isVirtualMeeting}
						className={css(styleSheet.radio)}
						disabled={meeting?.isBusy || meeting?.isSaving}
						id='meeting-config-location-virtual'
						name='meeting-config-location'
						onChange={onLocationChange(MeetingType.Virtual)}
					>
						<span>Virtual</span>
					</RadioButton>
					{isVirtualMeeting && (
						<div className={css(styleSheet.locationInfoContainer)}>
							{supportsUniqueLink && (
								<div className={css(styleSheet.checkboxContainer)}>
									<Checkbox
										checked={generateUniqueLink}
										id='meeting-config-location-virtual-generate-unique-link'
										onChange={onChangeGenerateUniqueLink}
									>
										<span
											className={css(styleSheet.checkboxText)}
										>{`Generate a unique ${uniqueLinkProviderName} link`}</span>
									</Checkbox>
								</div>
							)}
							{/* Show the manual link field unless a unique link will be generated. Also guard on supportsUniqueLink
							    so a stale generateUniqueLink (saved on a supported provider, then reconnected as an unsupported
							    one) can't blank out the whole section — those users must always be able to enter a link. */}
							{(!generateUniqueLink || !supportsUniqueLink) && (
								<>
									<p className={css(styleSheet.label)}>
										Please enter a virtual meeting link (Zoom, Google Hangout, etc) below:
									</p>
									<TextInput
										className={css(styleSheet.locationInfoInput)}
										type='text'
										inputId='meeting-config-location-virtual-text'
										value={virtualLink}
										onChange={onChangeVirtualLink}
										onBlur={onLocationChange(MeetingType.Virtual)}
									/>
								</>
							)}
						</div>
					)}
					<RadioButton
						checked={isMeetingAllowInvitee}
						className={css(styleSheet.radio)}
						disabled={meeting?.isBusy || meeting?.isSaving}
						id='meeting-config-location-allow-invitee'
						name='meeting-config-location'
						onChange={onLocationChange(MeetingType.AllowInvitee)}
					>
						<span>Allow invitee to decide</span>
					</RadioButton>
					{isMeetingAllowInvitee && (
						<div className={css(styleSheet.allowInviteeCheckboxes)}>
							<Checkbox
								checked={!!meeting?.allowInviteePhone}
								id='allow-invitee-phone'
								onChange={onAllowInviteeConfigUpdated(MeetingLocation.Phone)}
								className={css(styleSheet.radio)}
							>
								<span className={css(styleSheet.checkboxText)}>Phone Call</span>
							</Checkbox>
							<Checkbox
								checked={!!meeting?.allowInviteeInPerson}
								id='allow-invitee-inperson'
								onChange={onAllowInviteeConfigUpdated(MeetingLocation.InPerson)}
								className={css(styleSheet.radio)}
							>
								<span className={css(styleSheet.checkboxText)}>In Person</span>
							</Checkbox>
							{!!meeting?.allowInviteeInPerson && (
								<div className={css(styleSheet.locationInfoContainer)}>
									<p className={css(styleSheet.label)}>Please enter the physical meeting address below:</p>
									<TextInput
										className={css(styleSheet.locationInfoInput)}
										type='text'
										inputId='meeting-config-location-in-person-text'
										value={address}
										onChange={onChangeAddress}
										onBlur={updateAllowInviteeLocation(MeetingLocation.InPerson)}
									/>
								</div>
							)}
							<Checkbox
								checked={!!meeting?.allowInviteeVirtual}
								id='allow-invitee-virtual'
								onChange={onAllowInviteeConfigUpdated(MeetingLocation.Virtual)}
								className={css(styleSheet.radio)}
							>
								<span className={css(styleSheet.checkboxText)}>Virtual (please provide meeting link)</span>
							</Checkbox>
							{!!meeting?.allowInviteeVirtual && (
								<div className={css(styleSheet.locationInfoContainer)}>
									<p className={css(styleSheet.label)}>
										Please enter a virtual meeting link (Zoom, Google Hangout, etc) below:
									</p>
									<TextInput
										className={css(styleSheet.locationInfoInput)}
										type='text'
										inputId='meeting-config-location-virtual-text'
										value={virtualLink}
										onChange={onChangeVirtualLink}
										onBlur={updateAllowInviteeLocation(MeetingLocation.Virtual)}
									/>
								</div>
							)}
						</div>
					)}
				</div>
				<div className={css(styleSheet.sectionWrapper)}>
					<p className={css(styleSheet.label)}>Meeting Note</p>
					<RichContentDocumentEditor
						className={css(styleSheet.richContentEditor, styleSheet.noteEditor)}
						config={defaultEditorConfig}
						contentState={meetingNote}
						onBlur={onMeetingNoteBlur}
						onContentStateChanged={onMeetingNoteStateChange}
						readOnlyUseFullEditor={false}
					/>
					{showGenerateUniqueLinkHint && (
						<div className={css(styleSheet.uniqueLinkHint)}>
							<span className={css(styleSheet.uniqueLinkHintIcon)}>
								<InfoIcon />
							</span>
							<span>
								{`Heads up: this meeting type generates a unique ${uniqueLinkProviderName} link automatically, and the join details are added to the calendar invite for you. You don't need a location or meeting link merge field in the body — it's better used in confirmation or reminder emails.`}
							</span>
						</div>
					)}
				</div>
				<div className={css(styleSheet.sectionWrapper)}>
					<p className={css(styleSheet.label)}>
						Send a copy of meeting notifications to these emails (separate multiple emails with commas)
					</p>
					{ccEmailError && <span className={css(styleSheet.error)}>{ccEmailError}</span>}
					<TextInput
						inputId='meeting-config-cc'
						onBlur={onCcEmailBlur}
						onChange={onCcEmailChanged}
						type='text'
						value={ccEmail}
					/>
				</div>
			</div>
		</div>
	);
});
